import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import api, { fmtTRY } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["#0F172A", "#334155", "#64748B", "#94A3B8", "#CBD5E1", "#475569", "#1E293B", "#7C8BA0"];

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const names = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${names[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
};

function ChartCard({ title, children, testid, empty }) {
  return (
    <div className="rounded-md border border-border bg-card p-6" data-testid={testid}>
      <h2 className="mb-4 font-heading text-base font-bold">{title}</h2>
      {empty ? <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Veri yok</div> : children}
    </div>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then((r) => setData(r.data)); }, []);

  if (!data)
    return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-80 rounded-md" />)}</div>;

  const byMonth = data.by_month.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const cumulative = data.cumulative.map((m) => ({ ...m, label: monthLabel(m.month) }));

  const tip = { contentStyle: { borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13 }, formatter: (v) => fmtTRY(v) };

  return (
    <div className="animate-fade-up">
      <PageHeader overline="Analiz" title="Raporlar" subtitle="Şantiye giderlerinin detaylı analizi" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="İş Kalemine Göre Dağılım" testid="report-by-trade" empty={data.by_trade.length === 0}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={data.by_trade} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                {data.by_trade.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Aylık Gider (Histogram)" testid="report-monthly" empty={byMonth.length === 0}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byMonth} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}b` : v)} />
              <Tooltip {...tip} />
              <Bar dataKey="total" fill="#0F172A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Kümülatif Gider Eğrisi" testid="report-cumulative" empty={cumulative.length === 0}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cumulative} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}b` : v)} />
              <Tooltip {...tip} />
              <Line type="monotone" dataKey="total" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tedarikçiye Göre (İlk 10)" testid="report-by-supplier" empty={data.by_supplier.length === 0}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.by_supplier} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}b` : v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={90} />
              <Tooltip {...tip} />
              <Bar dataKey="total" fill="#334155" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
